import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Calendar, Clock, Mail, Menu, MessageSquare, PhoneCall, PhoneForwarded, PhoneOff, Users, Volume2 } from 'lucide-react';

// Blocks on the canvas, ported from the DialFlow prototype. Exit ids (handles) must match the backend engine's ports.

const wrapper = (selected, simulated) => `custom-node-wrapper ${selected ? 'node-selected' : ''} ${simulated ? 'node-sim-active' : ''}`;
const header = (rgb) => ({ background: `linear-gradient(135deg, rgba(${rgb}, 0.2) 0%, rgba(${rgb}, 0.05) 100%)` });
const In = () => <Handle type="target" position={Position.Left} id="input" className="custom-handle target-handle" />;

function TwoExits({ top, bottom }) {
  return (
    <>
      <Handle type="source" position={Position.Right} id={top[0]} className="custom-handle source-handle" style={{ top: '35%', background: 'var(--emerald-accent)' }} />
      <div className="handle-label-right" style={{ top: '35%', transform: 'translateY(-50%)', right: '10px' }}>{top[1]}</div>
      <Handle type="source" position={Position.Right} id={bottom[0]} className="custom-handle source-handle" style={{ top: '65%', background: 'var(--rose-accent)' }} />
      <div className="handle-label-right" style={{ top: '65%', transform: 'translateY(-50%)', right: '10px' }}>{bottom[1]}</div>
    </>
  );
}

const StartNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--cyan-accent)' }}>
    <div className="node-header" style={header('6, 182, 212')}>
      <PhoneCall size={16} className="node-icon" style={{ color: 'var(--cyan-accent)' }} />
      <span className="node-title">{data.label || 'Call Comes In'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Incoming Number:</span>
      <span className="node-preview-value">{data.phoneNumber || 'All company numbers'}</span>
    </div>
    <Handle type="source" position={Position.Right} id="output" className="custom-handle source-handle" style={{ background: 'var(--cyan-accent)' }} />
  </div>
));

const PlayAudioNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--violet-accent)' }}>
    <In />
    <div className="node-header" style={header('139, 92, 246')}>
      <Volume2 size={16} className={`node-icon ${data.isPlaying ? 'pulse-icon' : ''}`} style={{ color: 'var(--violet-accent)' }} />
      <span className="node-title">{data.label || 'Play Audio'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Type: {data.sourceType === 'url' ? 'Audio URL' : 'Text-To-Speech'}</span>
      <span className="node-preview-value text-ellipsis">{data.sourceType === 'url' ? (data.audioUrl || 'No URL set') : (data.message || 'No message set')}</span>
    </div>
    <Handle type="source" position={Position.Right} id="output" className="custom-handle source-handle" style={{ background: 'var(--violet-accent)' }} />
  </div>
));

const IVRMenuNode = memo(({ data, selected }) => {
  const options = data.options || [];
  const exits = [...options.map((o) => [`key-${o.key}`, o.key]), ['no-input', '—']];
  return (
    <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--emerald-accent)' }}>
      <In />
      <div className="node-header" style={header('16, 185, 129')}>
        <Menu size={16} className="node-icon" style={{ color: 'var(--emerald-accent)' }} />
        <span className="node-title">{data.label || 'IVR Menu'}</span>
      </div>
      <div className="node-body">
        <div className="node-preview-label text-ellipsis" style={{ marginBottom: '6px' }}>Prompt: "{data.message || 'Press a key...'}"</div>
        <div className="menu-branches-preview">
          {options.length ? options.map((o) => (
            <div key={o.key} className="menu-branch-tag"><span className="branch-key">{o.key}</span><span className="branch-label">{o.label}</span></div>
          )) : <span className="node-preview-empty">No keys configured</span>}
          <div className="menu-branch-tag" title="Caller pressed nothing"><span className="branch-key">—</span><span className="branch-label">No key</span></div>
        </div>
      </div>
      {exits.map(([id], idx) => (
        <Handle key={id} type="source" position={Position.Right} id={id} className="custom-handle source-handle"
          style={{ top: `${exits.length === 1 ? 50 : 20 + (idx / (exits.length - 1)) * 60}%`, background: id === 'no-input' ? 'var(--rose-accent)' : 'var(--emerald-accent)' }} />
      ))}
    </div>
  );
});

const OfficeHoursNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--amber-accent)' }}>
    <In />
    <div className="node-header" style={header('245, 158, 11')}>
      <Clock size={16} className="node-icon" style={{ color: 'var(--amber-accent)' }} />
      <span className="node-title">{data.label || 'Office Hours'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Timing Schedule:</span>
      <span className="node-preview-value">{data.hours?.start || '09:00'} - {data.hours?.end || '18:00'}</span>
      <span className="node-preview-label">{(data.days || []).length === 7 || !data.days ? 'Every day' : (data.days || []).map((d) => d[0].toUpperCase() + d.slice(1)).join(', ')}</span>
    </div>
    <TwoExits top={['open', 'Open']} bottom={['closed', 'Closed']} />
  </div>
));

const STRATEGY = { 'ring-all': 'Ring all', linear: 'One by one', 'round-robin': 'Round robin' };

const TeamNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--blue-accent)' }}>
    <In />
    <div className="node-header" style={header('59, 130, 246')}>
      <Users size={16} className="node-icon" style={{ color: 'var(--blue-accent)' }} />
      <span className="node-title">{data.label || 'Route to Team'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Queue: {data.queueName || 'Team'}</span>
      <span className="node-preview-value">{(data.members || []).length} members • {STRATEGY[data.strategy] || 'Ring all'}</span>
    </div>
    <TwoExits top={['answered', 'Answered']} bottom={['no-answer', 'No Answer']} />
  </div>
));

const CallForwardNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--blue-accent)' }}>
    <In />
    <div className="node-header" style={header('59, 130, 246')}>
      <PhoneForwarded size={16} className="node-icon" style={{ color: 'var(--blue-accent)' }} />
      <span className="node-title">{data.label || 'Call Forwarding'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Forward Target:</span>
      <span className="node-preview-value font-mono">{data.forwardNumber || 'No number set'}</span>
    </div>
    <TwoExits top={['answered', 'Answered']} bottom={['unanswered', 'Unanswered']} />
  </div>
));

const VoicemailNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--pink-accent)' }}>
    <In />
    <div className="node-header" style={header('236, 72, 153')}>
      <Mail size={16} className="node-icon" style={{ color: 'var(--pink-accent)' }} />
      <span className="node-title">{data.label || 'Voicemail'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Prompt text:</span>
      <span className="node-preview-value text-ellipsis">{data.message ? `"${data.message}"` : 'No message set'}</span>
    </div>
    <Handle type="source" position={Position.Right} id="output" className="custom-handle source-handle" style={{ background: 'var(--pink-accent)' }} />
  </div>
));

const SendSMSNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--indigo-accent)' }}>
    <In />
    <div className="node-header" style={header('99, 102, 241')}>
      <MessageSquare size={16} className="node-icon" style={{ color: 'var(--indigo-accent)' }} />
      <span className="node-title">{data.label || 'Send SMS'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Coming soon: can't be published yet</span>
      <span className="node-preview-value text-ellipsis">"{data.smsText || 'Welcome!'}"</span>
    </div>
    <Handle type="source" position={Position.Right} id="output" className="custom-handle source-handle" style={{ background: 'var(--indigo-accent)' }} />
  </div>
));

const CalendarNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--purple-accent)' }}>
    <In />
    <div className="node-header" style={header('168, 85, 247')}>
      <Calendar size={16} className="node-icon" style={{ color: 'var(--purple-accent)' }} />
      <span className="node-title">{data.label || 'Calendar / Holiday'}</span>
    </div>
    <div className="node-body">
      <span className="node-preview-label">Calendar: {data.holidayListName || 'Holidays'}</span>
      <span className="node-preview-value">{(data.holidays || []).length} dates configured</span>
    </div>
    <TwoExits top={['workday', 'Work Day']} bottom={['holiday', 'Holiday']} />
  </div>
));

const HangUpNode = memo(({ data, selected }) => (
  <div className={wrapper(selected, data.isSimulated)} style={{ borderColor: 'var(--rose-accent)', paddingBottom: '12px' }}>
    <In />
    <div className="node-header" style={header('239, 68, 68')}>
      <PhoneOff size={16} className="node-icon" style={{ color: 'var(--rose-accent)' }} />
      <span className="node-title">{data.label || 'Hung Up'}</span>
    </div>
    <div className="node-body"><span className="node-preview-label" style={{ color: 'var(--rose-accent)' }}>End of Call</span></div>
  </div>
));

export const nodeTypes = {
  start: StartNode,
  playAudio: PlayAudioNode,
  ivrMenu: IVRMenuNode,
  officeHours: OfficeHoursNode,
  team: TeamNode,
  callForward: CallForwardNode,
  voicemail: VoicemailNode,
  sendSMS: SendSMSNode,
  calendar: CalendarNode,
  hangUp: HangUpNode,
};

export const BLOCKS = [
  { type: 'start', label: 'Call Comes In', description: 'Entry point of the flow', icon: PhoneCall, color: 'rgba(6, 182, 212, 0.15)', borderColor: 'var(--cyan-accent)' },
  { type: 'playAudio', label: 'Play Audio', description: 'Play voice prompt or audio file', icon: Volume2, color: 'rgba(139, 92, 246, 0.15)', borderColor: 'var(--violet-accent)' },
  { type: 'ivrMenu', label: 'IVR Menu', description: 'Direct callers by key presses (0-9)', icon: Menu, color: 'rgba(16, 185, 129, 0.15)', borderColor: 'var(--emerald-accent)' },
  { type: 'officeHours', label: 'Office Hours', description: 'Route based on shop timings', icon: Clock, color: 'rgba(245, 158, 11, 0.15)', borderColor: 'var(--amber-accent)' },
  { type: 'team', label: 'Team', description: 'Ring your team members', icon: Users, color: 'rgba(59, 130, 246, 0.15)', borderColor: 'var(--blue-accent)' },
  { type: 'callForward', label: 'Call Forwarding', description: 'Redirect to external phone number', icon: PhoneForwarded, color: 'rgba(59, 130, 246, 0.15)', borderColor: 'var(--blue-accent)' },
  { type: 'voicemail', label: 'Voicemail', description: 'Record messages from caller', icon: Mail, color: 'rgba(236, 72, 153, 0.15)', borderColor: 'var(--pink-accent)' },
  { type: 'calendar', label: 'Calendar', description: 'Holiday or calendar schedules', icon: Calendar, color: 'rgba(168, 85, 247, 0.15)', borderColor: 'var(--purple-accent)' },
  { type: 'hangUp', label: 'Hung Up', description: 'End the phone call session', icon: PhoneOff, color: 'rgba(239, 68, 68, 0.15)', borderColor: 'var(--rose-accent)' },
  { type: 'sendSMS', label: 'Greeting SMS', description: 'Send text message back to caller', icon: MessageSquare, color: 'rgba(99, 102, 241, 0.15)', borderColor: 'var(--indigo-accent)', soon: true },
];

// Colours for connection lines, by the exit they leave from (as in the prototype).
export function edgeColor(sourceType, handle) {
  if (['open', 'answered', 'workday'].includes(handle) || handle?.startsWith('key-')) return 'var(--emerald-accent)';
  if (['closed', 'no-answer', 'unanswered', 'holiday', 'no-input'].includes(handle)) return 'var(--rose-accent)';
  return { start: 'var(--cyan-accent)', playAudio: 'var(--violet-accent)', voicemail: 'var(--pink-accent)', sendSMS: 'var(--indigo-accent)' }[sourceType] || 'rgba(148, 163, 184, 0.6)';
}

export function newNodeData(type) {
  switch (type) {
    case 'start': return { label: 'Call Comes In', phoneNumber: '' };
    case 'playAudio': return { label: 'Play Greeting Prompt', sourceType: 'text', message: 'Thank you for calling. We appreciate your patience.', voice: 'female', audioUrl: '' };
    case 'ivrMenu': return { label: 'IVR Keypad Router', message: 'Press 1 for service, press 2 for billing.', options: [{ key: '1', label: 'Service Option' }, { key: '2', label: 'Billing Option' }] };
    case 'officeHours': return { label: 'Business Hours', timezone: 'Asia/Kolkata', hours: { start: '09:00', end: '18:00' }, days: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'] };
    case 'team': return { label: 'Route to Team', queueName: 'Team', strategy: 'ring-all', members: [], ringTimeout: 20 };
    case 'callForward': return { label: 'External Call Forward', forwardNumber: '', ringTimeout: 20, announcement: 'Forwarding your call. Please hold.' };
    case 'voicemail': return { label: 'Record Voice Message', message: 'We are currently unavailable. Please leave a message after the beep.', emailDelivery: '' };
    case 'sendSMS': return { label: 'Send Notification SMS', smsText: 'Thank you for calling! We will contact you shortly.' };
    case 'calendar': return { label: 'Holiday Schedule Check', holidayListName: 'Holidays', holidays: [], timezone: 'Asia/Kolkata' };
    case 'hangUp': return { label: 'End Call', reason: 'completed' };
    default: return { label: type };
  }
}
